import { type ReactNode, useId } from "react";
import {
  BRAZIL_REGION_BOUNDS,
  BRAZIL_UF_SHAPES,
  type MapBounds,
  SAO_PAULO_BOUNDS,
  fitTransform,
} from "~/lib/domain/brazil-geometry";
import {
  BACKCHANNEL_SAO_PAULO_OFFICE,
  BRAZIL_REGION_CODES,
  BRAZIL_UF_REGIONS,
  type BrazilRegion,
  type BrazilUf,
} from "~/lib/domain/brazil-ufs";
import "./civic-art.css";

/**
 * "Território, documento e rastreabilidade": the regional and headquarters
 * card art. Flat SVG on paper — a UF territory crop, a document with one
 * cited line and an SH-style tile carrying the IBGE region code. Drawn from
 * the shared map geometry; no photo, no rate, no invented count.
 */

const VIEW_W = 320;
const VIEW_H = 160;
const TERRITORY_BOX: MapBounds = { x: 12, y: 12, width: 172, height: 136 };

const UF_COUNT_BY_REGION = Object.values(BRAZIL_UF_REGIONS).reduce<Record<BrazilRegion, number>>(
  (counts, region) => {
    counts[region] += 1;
    return counts;
  },
  { north: 0, northeast: 0, centralWest: 0, southeast: 0, south: 0 },
);

function pad(bounds: MapBounds, amount: number): MapBounds {
  return {
    x: bounds.x - amount,
    y: bounds.y - amount,
    width: bounds.width + amount * 2,
    height: bounds.height + amount * 2,
  };
}

type ArtLabel = { label?: string };

function ArtFrame({
  label,
  className,
  children,
}: ArtLabel & { className?: string; children: ReactNode }) {
  return (
    <svg
      className={className ? `civic-art ${className}` : "civic-art"}
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      preserveAspectRatio="xMidYMid meet"
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      focusable="false"
    >
      <rect className="civic-art__paper" width={VIEW_W} height={VIEW_H} />
      {children}
    </svg>
  );
}

function Territory({
  focus,
  isFocus,
  highlightUf,
}: {
  focus: MapBounds;
  isFocus: (uf: BrazilUf, region: BrazilRegion) => boolean;
  highlightUf?: BrazilUf | null;
}) {
  const clipId = useId();
  return (
    <g clipPath={`url(#${clipId})`}>
      <defs>
        <clipPath id={clipId}>
          <rect {...TERRITORY_BOX} rx={6} />
        </clipPath>
      </defs>
      <g transform={fitTransform(focus, TERRITORY_BOX)}>
        {BRAZIL_UF_SHAPES.map((shape) => {
          const inFocus = isFocus(shape.uf, shape.region);
          return (
            <path
              key={shape.uf}
              d={shape.path}
              className="civic-art__uf"
              data-focus={inFocus ? "true" : undefined}
              data-highlight={highlightUf === shape.uf ? "true" : undefined}
            />
          );
        })}
      </g>
    </g>
  );
}

/** Document with one cited (highlighted) line. */
const SHEET_LINES = [44, 34, 40, 26, 38, 30].map((length, index) => ({
  y: 17 + index * 10,
  length,
}));

function CitedSheet({ x, y }: { x: number; y: number }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <path className="civic-art__sheet" d="M0.5 0.5H52L63.5 12V87.5H0.5Z" />
      <path className="civic-art__ink-line" d="M52 0.5V12H63.5" />
      <rect className="civic-art__cite" x={7} y={33} width={49} height={8} rx={1.5} />
      <path className="civic-art__cite-mark" d="M4 32V42" />
      {SHEET_LINES.map((line) => (
        <path key={line.y} className="civic-art__text-line" d={`M9 ${line.y}H${9 + line.length}`} />
      ))}
    </g>
  );
}

/** SH/CEST-style tile carrying a code (IBGE region code or office floor). */
function CodeTile({
  x,
  y,
  code,
  caption,
}: { x: number; y: number; code: string; caption: string }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <rect className="civic-art__tile" x={0.5} y={0.5} width={43} height={43} rx={7} />
      <text className="civic-art__tile-code" x={22} y={23} textAnchor="middle">
        {code}
      </text>
      <text className="civic-art__tile-caption" x={22} y={36} textAnchor="middle">
        {caption}
      </text>
    </g>
  );
}

const REGION_MOTIF: Record<BrazilRegion, ReactNode> = {
  // River meanders.
  north: (
    <>
      <path d="M0 12C8 6 14 18 22 12S36 6 44 12 56 18 60 14" />
      <path d="M4 22C12 16 18 28 26 22S40 16 48 22" />
    </>
  ),
  // Sun on the coastline and a wave.
  northeast: (
    <>
      <path d="M14 18A10 10 0 0 1 34 18" />
      <path d="M24 3V5M13 8L14.5 9.5M35 8L33.5 9.5" />
      <path d="M0 18H60" />
      <path d="M2 25C8 21 12 29 18 25S28 21 34 25 44 29 50 25" />
    </>
  ),
  // Chapada: flat-topped tablelands.
  centralWest: (
    <>
      <path d="M0 26H60" />
      <path d="M4 26L12 12H30L36 26" />
      <path d="M32 26L38 17H50L56 26" />
    </>
  ),
  // City blocks.
  southeast: (
    <>
      <path d="M0 27H60" />
      <path d="M4 27V14H13V27M15 27V6H24V27M26 27V17H34V27M36 27V10H45V27M47 27V19H55V27" />
    </>
  ),
  // Araucárias.
  south: (
    <>
      <path d="M0 27H60" />
      <path d="M16 27V9M6 13C10 9 13 9 16 9S22 9 26 13M9 18C12 15 14 15 16 15S20 15 23 18" />
      <path d="M42 27V12M34 15C37 12 40 12 42 12S47 12 50 15M36 20C39 17 40 18 42 18S45 17 48 20" />
    </>
  ),
};

export function RegionArt({
  region,
  highlightUf = null,
  label,
  className,
}: ArtLabel & {
  region: BrazilRegion;
  highlightUf?: BrazilUf | null;
  className?: string;
}) {
  return (
    <ArtFrame label={label} className={className}>
      <Territory
        focus={pad(BRAZIL_REGION_BOUNDS[region], 10)}
        isFocus={(_uf, ufRegion) => ufRegion === region}
        highlightUf={highlightUf}
      />
      <CitedSheet x={238} y={16} />
      <CodeTile
        x={202}
        y={96}
        code={BRAZIL_REGION_CODES[region]}
        caption={`${UF_COUNT_BY_REGION[region]} UF`}
      />
      <g className="civic-art__motif" transform="translate(252 114)">
        {REGION_MOTIF[region]}
      </g>
    </ArtFrame>
  );
}

/** Vila Olímpia office: SP territory with the HQ point and the building elevation. */
export function HeadquartersArt({ label, className }: ArtLabel & { className?: string }) {
  const point = BACKCHANNEL_SAO_PAULO_OFFICE.mapPoint;
  const focus = pad(SAO_PAULO_BOUNDS, 12);
  const floors = 8;
  const columns = 4;
  const tower = { x: 222, y: 20, width: 76, height: 124 };
  const cell = { width: 12, height: 10 };
  const gapX = (tower.width - columns * cell.width) / (columns + 1);
  // Conj. 31A: third floor, first unit. Floors count from the ground up.
  const officeFloor = 3;
  const floorY = (floor: number) => tower.y + tower.height - 26 - floor * 12;
  const windows = Array.from({ length: floors * columns }, (_, slot) => {
    const floor = Math.floor(slot / columns) + 1;
    const column = slot % columns;
    return {
      id: `floor-${floor}-unit-${column}`,
      office: floor === officeFloor && column === 0,
      x: tower.x + gapX + column * (cell.width + gapX) + 0.5,
      y: floorY(floor) + 0.5,
    };
  });

  return (
    <ArtFrame label={label} className={className}>
      <Territory focus={focus} isFocus={(uf) => uf === "SP"} />
      <g transform={fitTransform(focus, TERRITORY_BOX)}>
        <circle
          className="civic-art__hq-point"
          cx={point.x}
          cy={point.y}
          r={4.2}
          vectorEffect="non-scaling-stroke"
        />
      </g>
      <g className="civic-art__building">
        <path className="civic-art__ground" d={`M200 ${tower.y + tower.height}.5H312`} />
        <rect
          className="civic-art__tower"
          x={tower.x + 0.5}
          y={tower.y + 0.5}
          width={tower.width}
          height={tower.height}
          rx={2}
        />
        {windows.map((pane) => (
          <rect
            key={pane.id}
            className={
              pane.office ? "civic-art__window civic-art__window--office" : "civic-art__window"
            }
            x={pane.x}
            y={pane.y}
            width={cell.width}
            height={cell.height - 2}
            rx={1}
          />
        ))}
        <rect
          className="civic-art__door"
          x={tower.x + tower.width / 2 - 7 + 0.5}
          y={tower.y + tower.height - 16 + 0.5}
          width={14}
          height={16}
          rx={1}
        />
        <path
          className="civic-art__ink-line"
          d={`M${tower.x - 6} ${floorY(officeFloor) + 4.5}H${tower.x - 1}`}
        />
        <text
          className="civic-art__tile-caption civic-art__tile-caption--ink"
          x={tower.x - 8}
          y={floorY(officeFloor) + 7.5}
          textAnchor="end"
        >
          31A
        </text>
      </g>
    </ArtFrame>
  );
}
